import { z } from "zod";

export const FEATURE_PAGE_VIEW_TYPES = [
  "title",
  "subtitle",
  "paragraph",
  "bulletedParagraph",
  "caption",
  "imageUrl",
  "button",
  "grid",
  "padding",
] as const;

export const FEATURE_PAGE_BUTTON_STYLES = ["accent", "link"] as const;

export const FEATURE_PAGE_SIZES = [
  "zero",
  "mini",
  "small",
  "regular",
  "medium",
  "large",
  "extraLarge",
] as const;

export const FEATURE_PAGE_ORIENTATIONS = ["vertical", "horizontal"] as const;

export const FEATURE_PAGE_ALIGNMENTS = [
  "leading",
  "trailing",
  "top",
  "bottom",
] as const;

export type FeaturePageViewType = (typeof FEATURE_PAGE_VIEW_TYPES)[number];
export type FeaturePageButtonStyle =
  (typeof FEATURE_PAGE_BUTTON_STYLES)[number];
export type FeaturePageSize = (typeof FEATURE_PAGE_SIZES)[number];
export type FeaturePageOrientation =
  (typeof FEATURE_PAGE_ORIENTATIONS)[number];
export type FeaturePageAlignment =
  (typeof FEATURE_PAGE_ALIGNMENTS)[number];

export interface FeaturePageButton {
  style: FeaturePageButtonStyle;
  size: FeaturePageSize;
  action: string;
}

export interface FeaturePageGrid {
  orientation: FeaturePageOrientation;
  minimumSize: number;
  maximumSize: number;
  spacing: number;
  content: FeaturePage[];
}

/**
 * The camelCase JSON shape decoded by the iOS `FeaturePage` model.
 *
 * Optional fields intentionally stay optional: Swift's `Codable` model permits
 * sparse container and leaf nodes, and the renderer ignores incomplete leaves.
 */
export interface FeaturePage {
  id: string;
  columns?: FeaturePage[];
  rows?: FeaturePage[];
  viewType?: FeaturePageViewType;
  level?: number;
  content?: string;
  button?: FeaturePageButton;
  grid?: FeaturePageGrid;
  alignment?: FeaturePageAlignment;
}

export type FeaturePageUrlKind = "link" | "image";

const SAFE_LINK_PROTOCOLS = new Set(["https:", "mailto:", "tel:", "petcollab:"]);
const SAFE_IMAGE_PROTOCOLS = new Set(["https:"]);

function containsControlCharacter(value: string) {
  return Array.from(value).some((character) => {
    const codePoint = character.codePointAt(0) ?? 0;
    return codePoint <= 31 || codePoint === 127;
  });
}

/**
 * Allows same-site relative URLs plus an explicit protocol allowlist.
 * Protocol-relative URLs are rejected so a payload cannot silently choose an
 * arbitrary host. Images are intentionally limited to HTTPS or local paths.
 */
export function isSafeFeaturePageUrl(
  value: string,
  kind: FeaturePageUrlKind = "link",
): boolean {
  const candidate = value.trim();

  if (!candidate || containsControlCharacter(candidate)) {
    return false;
  }

  if (
    candidate.startsWith("/") &&
    !candidate.startsWith("//") &&
    !candidate.startsWith("/\\")
  ) {
    return true;
  }

  if (kind === "link" && (candidate.startsWith("#") || candidate.startsWith("?"))) {
    return true;
  }

  try {
    const url = new URL(candidate);
    const protocols = kind === "image" ? SAFE_IMAGE_PROTOCOLS : SAFE_LINK_PROTOCOLS;
    return protocols.has(url.protocol.toLowerCase());
  } catch {
    return false;
  }
}

/** Returns a trimmed URL only when it is safe for the requested context. */
export function getSafeFeaturePageUrl(
  value: string | null | undefined,
  kind: FeaturePageUrlKind = "link",
): string | undefined {
  if (typeof value !== "string" || !isSafeFeaturePageUrl(value, kind)) {
    return undefined;
  }

  return value.trim();
}

const featurePageButtonSchema: z.ZodType<FeaturePageButton> = z.object({
  style: z.enum(FEATURE_PAGE_BUTTON_STYLES),
  size: z.enum(FEATURE_PAGE_SIZES),
  action: z
    .string()
    .refine((value) => isSafeFeaturePageUrl(value, "link"), {
      message: "Button action must use an allowed URL scheme.",
    })
    .transform((value) => value.trim()),
});

export const featurePageGridSchema: z.ZodType<FeaturePageGrid> = z.lazy(() =>
  z.object({
    orientation: z.enum(FEATURE_PAGE_ORIENTATIONS),
    minimumSize: z.number().nonnegative(),
    maximumSize: z.number().nonnegative(),
    spacing: z.number().nonnegative(),
    content: z.array(featurePageSchema),
  }),
);

export const featurePageSchema: z.ZodType<FeaturePage> = z.lazy(() =>
  z
    .object({
      id: z.string(),
      columns: z.array(featurePageSchema).optional(),
      rows: z.array(featurePageSchema).optional(),
      viewType: z.enum(FEATURE_PAGE_VIEW_TYPES).optional(),
      level: z.number().int().optional(),
      content: z.string().optional(),
      button: featurePageButtonSchema.optional(),
      grid: featurePageGridSchema.optional(),
      alignment: z.enum(FEATURE_PAGE_ALIGNMENTS).optional(),
    })
    .superRefine((page, context) => {
      if (
        page.viewType === "imageUrl" &&
        page.content !== undefined &&
        !isSafeFeaturePageUrl(page.content, "image")
      ) {
        context.addIssue({
          code: "custom",
          message: "Image content must use HTTPS or a same-site path.",
          path: ["content"],
        });
      }

      if (
        page.grid !== undefined &&
        page.grid.maximumSize < page.grid.minimumSize
      ) {
        context.addIssue({
          code: "custom",
          message: "Grid maximumSize must be greater than or equal to minimumSize.",
          path: ["grid", "maximumSize"],
        });
      }
    }),
);

export const featurePageCacheSchema = z.object({
  version: z.literal(1),
  page: featurePageSchema,
});

/** Parses either a decoded JSON value or an Unleash string payload. */
export function parseFeaturePage(value: unknown) {
  let decoded = value;

  if (typeof value === "string") {
    try {
      decoded = JSON.parse(value) as unknown;
    } catch {
      return featurePageSchema.safeParse(undefined);
    }
  }

  return featurePageSchema.safeParse(decoded);
}

/**
 * Unleash information flags use their English name as the base flag and a
 * `.es` suffix for Spanish content.
 */
export function resolveFeaturePageFlagName(
  englishBaseName: string,
  locale: string,
): string {
  const baseName = englishBaseName.trim();
  const language = locale.trim().toLowerCase().split(/[-_]/, 1)[0];

  if (language === "es" && !baseName.endsWith(".es")) {
    return `${baseName}.es`;
  }

  return baseName;
}
